import { useEffect, useId, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, FolderTree } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useEpicOwnerTeams } from "@/hooks/queries/use-teams";

interface DisciplinePickerProps {
  value?: string;
  onChange: (teamId: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  description?: string;
}

export function DisciplinePicker({
  value,
  onChange,
  label = "Team / Product",
  placeholder = "Select a discipline…",
  disabled = false,
  description,
}: DisciplinePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: teams, isLoading } = useEpicOwnerTeams();

  const selectedTeam = useMemo(
    () => teams?.find((team) => team.id === value),
    [teams, value],
  );

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setSearch("");
    }
  }, [open]);

  const filteredTeams = useMemo(() => {
    if (!teams) return [];
    if (!search.trim()) return teams;

    const query = search.toLowerCase();
    return teams.filter((team) => {
      return (
        team.name.toLowerCase().includes(query) ||
        team.key?.toLowerCase().includes(query) ||
        team.fullPath.toLowerCase().includes(query)
      );
    });
  }, [search, teams]);

  const labelId = useId();

  return (
    <div className="grid gap-2">
      <Label id={labelId}>{label}</Label>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            aria-expanded={open}
            aria-labelledby={labelId}
            className={cn(
              "w-full items-center justify-between gap-2 px-3 text-left font-normal",
              !selectedTeam && "text-muted-foreground",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <FolderTree className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {selectedTeam ? selectedTeam.fullPath : placeholder}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            className="z-50 w-[min(32rem,var(--radix-popover-trigger-width))] rounded-lg border bg-popover shadow-md outline-none animate-in fade-in-0 zoom-in-95"
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <Command shouldFilter={false}>
              <CommandInput
                ref={inputRef}
                placeholder="Search disciplines…"
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                <CommandEmpty>
                  {isLoading ? "Loading disciplines…" : "No epic owner teams found."}
                </CommandEmpty>
                <CommandGroup>
                  {filteredTeams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={team.id}
                      onSelect={() => {
                        onChange(team.id);
                        setOpen(false);
                      }}
                      className="items-start gap-3 py-2"
                    >
                      <Check
                        className={cn(
                          "mt-0.5 h-4 w-4 shrink-0",
                          value === team.id ? "opacity-100 text-primary" : "opacity-0",
                        )}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{team.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {team.fullPath}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
